// The scrub dock (HD-8, DESIGN rev 2.1 §3.2): the Production page's right panel, 360 px, on src/ui. The current scrub as
// one line (vN and date), Download XML for Premiere or Resolve (originals or proxies when a clip is flagged BRAW), the
// original-format checklist, Render to MP4 with Preview and Download, the "Tell the editor what to fix" box with Scrub
// beside it (Assembly and the Admin fresh presses under More), and the older scrubs (View opens one read-only).
// Internal users only: the page does not mount it for external accounts.
//
// Engine state the editor acts on reads as Badges in the dock head: queued (position N), scrubbing (elapsed), failed,
// and the current cut list. Render state sits on the Render button and in one line when it fails.
//
// Retired at HD-8 (Owner ruling: readiness and progress reports leave the product):
//   - the readiness strip and its "Prepare clips" button (GET /api/cut-lists/card/:id/readiness, polled every 8 s while
//     a clip was in flight; POST /api/cut-lists/card/:id/provision);
//   - the readiness checklist shown when a press is refused with code scrub_not_ready (its fix links and per-item ticks):
//     a refusal is now one line, "The scrub could not start." and the server's message, plus "Scrub anyway" when every open
//     item can be acknowledged; it sends the same build body the old checklist sent with every item ticked (HD-9 0a: after a
//     short confirm that names what it acknowledges);
//   - the Shoot stage's coverage section (coverage map, scenes covered, scenes called on set, reaction rows, overridden rows
//     and "Mark speaking", PUT /api/cut-lists/card/:id/scene/:sceneId/override);
//   - the Missed lines table under the current scrub (read from the cut-list payload, no call of its own) and
//     MissedLinesForCard (GET /api/missed-lines/card/:id).
//
// Calls kept, same params: GET /api/cut-lists/card/:id; GET …/scrub-status (every 5 s while queued or working);
// POST …/build { correction? , uncastCharacters?, ungroupedRowsAcknowledged?, fresh?, mode? }; DELETE …/queue;
// POST /api/cut-lists/:id/render and GET …/render (every 5 s while a render runs); GET /api/cut-lists/:id/download
// [?media=proxies]; PATCH /api/footage/:id { originalExt }.
import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronDown, ChevronsRight, Download, Eye, Film, History, Play, RotateCcw, Undo2, X } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Checkbox from '../ui/Checkbox';
import Icon from '../ui/Icon';
import Menu from '../ui/Menu';
import Modal from '../ui/Modal';
import Popover from '../ui/Popover';
import { clean } from '../shell/copy';
import VideoPlayer from './VideoPlayer';

// HD-11: the Assembly presses (D-185) ship only where the API has Assembly; a build without REACT_APP_ASSEMBLY=true hides them.
const ASSEMBLY = process.env.REACT_APP_ASSEMBLY === 'true';
const MAX_CORRECTION = 500;                                                  // E-2: the API refuses longer (journey-viral-api/src/lib/correction.js)
const fmtElapsed = s => { s = Math.max(0, Math.floor(Number(s) || 0)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };
const fmtDT = s => s ? new Date(s).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'no date';
const firstLine = v => String(v).split('\n')[0];

// D-175 BG-2: a stored failure from the server, as the panel's one plain line (a watcher build says it was the auto-build)
const failureLine = f => `${f.trigger === 'auto' ? 'Auto-build stopped: ' : ''}${firstLine(f.error || 'The scrub failed. Nothing was stored.')}`;
// The build body a refused press needs to go through: every open item acknowledged (the old checklist's "acknowledging these"
// with every item ticked). Only offered when no open item is hard.
function acknowledgeAll(refusal) {
  const failing = (refusal.checklist || []).filter(c => !c.ok);
  const items = failing.flatMap(c => (c.items || []).map(it => ({ check: c.key, item: it })));
  return {
    uncastCharacters: items.filter(a => a.check === 'cast').map(a => a.item.characterId || a.item.character),
    ungroupedRowsAcknowledged: items.filter(a => a.check === 'segmented').flatMap(a => a.item.rows),
  };
}
// HD-9 0a (Owner ruling): what Scrub anyway acknowledges, as names for its confirm (no counts, no percentages): the characters
// left uncast and the characters whose rows have no shoot segment, each name once.
function acknowledgedNames(refusal) {
  const failing = (refusal.checklist || []).filter(c => !c.ok);
  const names = key => [...new Set(failing.filter(c => c.key === key).flatMap(c => (c.items || []).map(it => it.character)).filter(Boolean))];
  return [
    { label: 'Left uncast', names: names('cast') },
    { label: 'Rows with no shoot segment', names: names('segmented') },
  ].filter(g => g.names.length);
}
const RENDER_LIVE = ['queued', 'stitching', 'measuring', 'cutaways', 'rendering'];   // B-1: 'cutaways' = the video-only B-roll pass; R2 (DEF-118): pass 1 stitches, pass 2 applies one gain

export default function SceneScrubPanel({ cardId, canBuild, onBuilt, sceneRange = null, onCollapse = null }) {
  // FRESH SCRUB (Readiness Arc A-6): Admins get "Fresh scrub" under More (the { fresh: true } press), behind a confirmation.
  const { user } = useAuth() || {};
  const isAdmin = !!(user && Array.isArray(user.roles) && user.roles.includes('Admin'));
  const [moreOpen, setMoreOpen] = useState(false);
  const [freshAsk, setFreshAsk] = useState(false);
  const [assemblyAsk, setAssemblyAsk] = useState(false);              // D-185: Fresh Assembly Scrub confirmation
  const [refusal, setRefusal] = useState(null);                       // the last scrub_not_ready refusal, and the press it answered
  const [anywayAsk, setAnywayAsk] = useState(false);                  // HD-9 0a: Scrub anyway asks once, naming what it acknowledges
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  // C-2: which stored version the panel shows (null = the current / latest); the history control
  const [viewing, setViewing] = useState(null);
  const [histOpen, setHistOpen] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [scrubErr, setScrubErr] = useState('');
  const [correction, setCorrection] = useState('');
  const [origOpen, setOrigOpen] = useState(false);       // BRAW originals (B-3): the per-clip checklist under the Originals line
  const [origBusy, setOrigBusy] = useState(false);
  const [origErr, setOrigErr] = useState('');
  const startedAt = useRef(0);
  // Q-4 (D-179): a Scrub press ENQUEUES. The panel follows the card's slot in the queue from the server: queued (position N,
  // a cancel for the person who queued it), working (elapsed), failed (one plain line). No counts, no dollars.
  const [slot, setSlot] = useState(null);                 // null | { state: 'queued', position, canCancel } | { state: 'working', startedAt }
  const running = !!slot && slot.state === 'working';
  const queued = !!slot && slot.state === 'queued';
  const pressedHere = useRef(false);

  const adoptStatus = useCallback((st) => {
    if (!st) return;
    if (st.state === 'working') { startedAt.current = Date.parse(st.startedAt) || Date.now(); setElapsed(st.elapsedSec || 0); setSlot(st); setScrubErr(''); return; }
    if (st.state === 'queued') { setSlot(st); setScrubErr(''); return; }
    setSlot(null);
    if (st.state === 'failed' && st.error) setScrubErr(failureLine(st)); else if (st.state === 'idle') setScrubErr('');
  }, []);
  const load = useCallback(async () => {
    try { const a = await api.get(`/api/cut-lists/card/${cardId}`); setData(a.data); setErr(''); adoptStatus(a.data?.scrub); }
    catch (e) { setErr(e.response?.data?.error || 'The scrub did not load.'); }
  }, [cardId, adoptStatus]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setElapsed(Math.round((Date.now() - startedAt.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, [running]);
  // while the card is queued or working, poll the cheap status endpoint; when the slot clears, load the stored version (or show the line)
  useEffect(() => {
    if (!slot) return;
    let stop = false;
    const t = setInterval(async () => {
      try {
        const { data: st } = await api.get(`/api/cut-lists/card/${cardId}/scrub-status`);
        if (stop) return;
        if (st.state === 'queued' || st.state === 'working') { adoptStatus(st); return; }
        setSlot(null); setViewing(null); await load(); onBuilt && onBuilt();
        if (st.state === 'failed' && st.error) setScrubErr(failureLine(st));
      } catch { /* transient: the next tick retries */ }
    }, 5000);
    return () => { stop = true; clearInterval(t); };
  }, [slot, cardId, load, onBuilt, adoptStatus]);

  // A refused press (code scrub_not_ready) is one line, never a list. `acks` = every open item acknowledged ("Scrub anyway").
  // D-185: `assembly` = the deterministic script-walk mode; no correction rides it.
  async function scrub(acks = null, fresh = false, assembly = false) {
    const text = fresh || assembly ? '' : correction.trim();
    pressedHere.current = true; setScrubErr(''); setElapsed(0); startedAt.current = Date.now();
    try {
      const { data: st } = await api.post(`/api/cut-lists/card/${cardId}/build`, { ...(text ? { correction: text } : {}), ...(acks || {}), ...(fresh ? { fresh: true } : {}), ...(assembly ? { mode: 'assembly' } : {}) });
      if (!fresh && !assembly) setCorrection(''); setViewing(null); setRefusal(null); adoptStatus(st);
      if (st.note) setScrubErr(st.note);
    } catch (e) {
      const d = e.response?.data;
      if (d?.code === 'scrub_not_ready' && Array.isArray(d.checklist)) { setRefusal({ body: d, fresh, assembly }); return; }
      setRefusal(null); setScrubErr(firstLine(d?.error || 'The scrub failed. Nothing was stored.'));
    }
    finally { pressedHere.current = false; }
  }
  async function cancelQueued() {
    try { const { data: st } = await api.delete(`/api/cut-lists/card/${cardId}/queue`); adoptStatus(st); if (st.state === 'idle') setSlot(null); }
    catch (e) { setScrubErr(firstLine(e.response?.data?.error || 'The queued scrub was not cancelled. Try again.')); }
  }
  // Render v1 (D-163): the cut to MP4 in MediaConvert: fire, poll, play, download
  const [renderRec, setRenderRec] = useState(null);          // the record of the version on show (from the card payload, refreshed by polling)
  const [renderBusy, setRenderBusy] = useState(false);
  const [showRender, setShowRender] = useState(false);
  const [renderElapsed, setRenderElapsed] = useState(0);     // R-1: counts from the record's own submit time (survives a reload)
  const [renderStartErr, setRenderStartErr] = useState('');
  useEffect(() => {
    if (!renderRec || !RENDER_LIVE.includes(renderRec.status) || !renderRec.submittedAt) return;
    const tick = () => setRenderElapsed(Math.max(0, Math.round((Date.now() - new Date(renderRec.submittedAt).getTime()) / 1000))); tick();
    const t = setInterval(tick, 1000); return () => clearInterval(t);
  }, [renderRec?.status, renderRec?.submittedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const cl = viewing != null ? (data?.cutLists || []).find(c => c.version === viewing) : data?.cutLists?.[0]; setRenderRec(cl?.stats?.render || null); }, [data, viewing]);
  useEffect(() => {
    if (!renderRec || !RENDER_LIVE.includes(renderRec.status)) return;
    const cl = viewing != null ? (data?.cutLists || []).find(c => c.version === viewing) : data?.cutLists?.[0]; if (!cl) return;
    const t = setInterval(async () => { try { const r = await api.get(`/api/cut-lists/${cl.id}/render`); setRenderRec(r.data); if (!RENDER_LIVE.includes(r.data.status)) { clearInterval(t); load(); } } catch { /* next tick */ } }, 5000);
    return () => clearInterval(t);
  }, [renderRec?.status, renderRec?.jobId, data, viewing, load]); // eslint-disable-line react-hooks/exhaustive-deps -- re-arm on status / job change only, not on every poll tick
  async function startRender(cl) {
    setRenderBusy(true); setRenderStartErr('');
    try { const r = await api.post(`/api/cut-lists/${cl.id}/render`); setRenderRec(r.data); setShowRender(false); }
    catch (e) { setRenderStartErr(firstLine(e.response?.data?.error || 'the render could not start')); }
    finally { setRenderBusy(false); }
  }
  // BRAW originals (B-4): media = 'proxies' downloads the XML as stored (.mp4 names as uploaded); the default names each flagged
  // clip's .braw original. A card with no flagged clip has one button and gets exactly the stored bytes.
  async function download(cl, media = null) {
    try {
      const res = await api.get(`/api/cut-lists/${cl.id}/download${media === 'proxies' ? '?media=proxies' : ''}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data); const a = document.createElement('a');
      a.href = url; a.download = `${(data?.card?.scriptName || 'cut-list').replace(/[^a-zA-Z0-9._-]+/g, '_')}-cutlist-v${cl.version}${media === 'proxies' ? '-proxies' : ''}.xml`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { setErr('The XML did not download. Try again.'); }
  }
  // B-3: the per-clip original-format checklist; each toggle is the same PATCH /api/footage/:id the Raw footage clip panel uses
  async function setOriginals(ids, ext) {
    setOrigBusy(true); setOrigErr('');
    try { for (const id of ids) await api.patch(`/api/footage/${id}`, { originalExt: ext }); await load(); }
    catch (e) { setOrigErr(firstLine(e.response?.data?.error || 'The original format was not saved. Try again.')); await load(); }
    finally { setOrigBusy(false); }
  }

  // C-2: the version on show: the latest by default, or an older one opened read-only from the history
  const current = data?.cutLists?.[0] || null;
  const viewed = viewing != null ? (data?.cutLists || []).find(c => c.version === viewing) || null : null;
  const ro = !!viewed;                                                        // read-only: no Scrub, no correction
  const shown = viewed || current;
  const act = canBuild && !ro;
  const canPin = act && !!data?.canPinBroll;                                  // the render gate (Producer / Power User / Admin, internal)
  // E-1 (c): the version line, `vN, date`; D-185: an assembly version says so
  const assemblyNote = cl => cl.stats?.mode === 'assembly' ? `, assembly${cl.stats.assembly?.summary ? ` ${cl.stats.assembly.summary.placed}/${cl.stats.assembly.summary.spokenRows} lines` : ''}` : '';
  const orig = data?.originals || null;
  const hasBraw = !!orig && orig.braw > 0;
  const origLine = !orig || !orig.total ? null : hasBraw ? `Originals: ${orig.braw} of ${orig.total} BRAW` : orig.clips.every(c => c.proxyOk) ? 'Originals: all MP4' : 'Originals: as uploaded';
  const xmlButtons = (cl, history) => {
    if (!hasBraw) return (
      <Button variant={history ? 'quiet' : 'secondary'} icon={Download} data-testid="scene-scrub-download" data-media="as-uploaded" onClick={() => download(cl)}
        title={history ? 'One FCP7 XML: import into Premiere Pro or DaVinci Resolve' : 'One FCP7 XML: import into Premiere Pro or DaVinci Resolve; media relinks from HELICONFLOW/<card>/<file>'}>
        {history ? 'XML' : 'Download XML'}
      </Button>
    );
    return <>
      <Button variant={history ? 'quiet' : 'secondary'} icon={Download} data-testid="scene-scrub-download" data-media="originals" onClick={() => download(cl)}
        title="One FCP7 XML naming the BRAW originals (.braw) for the flagged clips: relinks the originals on your machine">{history ? 'XML, originals' : 'Download XML, originals (.braw)'}</Button>
      <Button variant="quiet" icon={Download} data-testid={history ? 'cutlist-history-download-proxies' : 'scene-scrub-download-proxies'} data-media="proxies" onClick={() => download(cl, 'proxies')}
        title="The same XML with the uploaded proxies' .mp4 names">{history ? 'XML, proxies' : 'Proxies (.mp4, as uploaded)'}</Button>
    </>;
  };
  const correctionOf = cl => (cl?.stats?.correction && cl.stats.correction.text) || null;
  const rangeNote = sceneRange ? `${sceneRange.episode != null ? `episode ${sceneRange.episode}, ` : ''}${sceneRange.from != null || sceneRange.to != null ? `scenes ${sceneRange.from ?? 'start'} to ${sceneRange.to ?? 'end'}` : 'this episode'}` : null;
  const renderLive = !!(renderRec && RENDER_LIVE.includes(renderRec.status));
  const renderDone = !!(renderRec && renderRec.status === 'done' && renderRec.url);
  const ackable = refusal && (refusal.body.checklist || []).some(c => !c.ok) && (refusal.body.checklist || []).filter(c => !c.ok).every(c => !c.hard);
  const moreItems = [
    isAdmin && { label: 'Fresh scrub', testId: 'scrub-fresh', disabled: !!correction.trim(), onSelect: () => setFreshAsk(true),
      title: correction.trim() ? 'A fresh scrub takes no correction. Clear the box first.' : 'Build from scratch: no cached results from earlier versions, no editor picks' },
    ASSEMBLY && data?.canAssembly && { label: 'Assembly scrub', testId: 'scrub-assembly', disabled: !!correction.trim(), onSelect: () => scrub(null, false, true),
      title: correction.trim() ? 'An assembly scrub takes no correction. Clear the box first.' : 'Walk the script row by row, cut each line from its camera, pick takes by shoot rules, leave every doubt as a gap. Usually well under $1.' },
    ASSEMBLY && isAdmin && { label: 'Fresh assembly scrub', testId: 'scrub-assembly-fresh', disabled: !!correction.trim(), onSelect: () => setAssemblyAsk(true),
      title: correction.trim() ? 'An assembly scrub takes no correction. Clear the box first.' : 'Assembly from scratch: no reused span labels or locator reads from earlier assembly versions' },
  ].filter(Boolean);

  return (
    <div className="ui-chat" data-testid="scene-scrub-section-cutlists">
      <div className="ui-drawer__head">
        <h2 className="ui-heading">Scrub</h2>
        {running ? <Badge tone="info">Scrubbing {fmtElapsed(elapsed)}</Badge>
          : queued ? <Badge tone="info">Queued, position {slot.position || 1}</Badge>
          : scrubErr ? <Badge tone="danger">Failed</Badge>
          : current ? <><span className="ui-tc">v{current.version}</span><Badge tone="ok">Ready</Badge></> : null}
        {onCollapse && <Button variant="quiet" iconOnly icon={ChevronsRight} label="Collapse the scrub dock" onClick={onCollapse} />}
      </div>
      <div className="ui-thread ui-thread--drawer ui-stack">
        {err && <span className="ui-field__error" role="alert">{clean(err)}</span>}
        {!data && !err && <p className="ui-p ui-meta" role="status">Loading the scrub</p>}

        {data && shown && (
          <section data-testid="scene-scrub-cutlists" data-viewing={ro ? String(shown.version) : 'current'} className="ui-stack" aria-label={`Scrub v${shown.version}`}>
            <div data-testid="cutlist-current-row" className="ui-stack ui-stack--tight">
              <span className="ui-cluster">
                <span data-testid="cutlist-editor-line" className="ui-dense"
                  title={shown.stats?.sequences?.count === 2 ? 'One file, two sequences: 01 ROUGH CUT (first-pass edit) and 02 ALL TAKES (every take by scene)' : 'One file, one sequence: ROUGH CUT (first-pass edit)'}>
                  <span className="ui-tc">v{shown.version}</span> {fmtDT(shown.builtAt)}{assemblyNote(shown)}
                </span>
                {ro && <span data-testid="cutlist-viewing-marker"><Badge tone="warn">Viewing v{shown.version}, not current</Badge></span>}
              </span>
              {ro && <span><Button variant="quiet" icon={Undo2} data-testid="cutlist-back-to-current" onClick={() => setViewing(null)}>Back to current (v{current?.version})</Button></span>}
            </div>
            <span className="ui-cluster">
              {xmlButtons(shown, false)}
              {/* R-1: Render the current scrub to MP4; done: Preview (play here) and Download MP4; a failure is one plain line */}
              {!ro && canPin && (
                <Button variant="secondary" icon={Film} data-testid="cutlist-render-button" data-state={renderLive ? 'running' : renderRec && renderRec.status === 'done' ? 'done' : 'idle'}
                  disabled={renderBusy || renderLive} loading={renderBusy}
                  title="Render this scrub to one MP4 in AWS MediaConvert: the rough cut as built, holes as 2 s missing-scene slates; a re-render replaces this version's preview"
                  onClick={() => startRender(shown)}>
                  {renderLive ? `Rendering ${fmtElapsed(renderElapsed)}` : renderRec && renderRec.status === 'done' ? 'Re-render' : 'Render'}
                </Button>
              )}
              {renderDone && <>
                <Button variant="quiet" icon={showRender ? X : Play} data-testid="cutlist-render-play" onClick={() => setShowRender(v => !v)}>{showRender ? 'Hide preview' : 'Preview'}</Button>
                <a data-testid="cutlist-render-download" className="ui ui-btn ui-btn--quiet" href={renderRec.url} download={`${(data?.card?.scriptName || 'cut').replace(/[^a-zA-Z0-9._-]+/g, '_')}-v${shown.version}.mp4`} target="_blank" rel="noreferrer">
                  <Icon icon={Download} />Download MP4
                </a>
              </>}
            </span>
            {renderLive && <span><Badge tone="info">Render {renderRec.status}</Badge></span>}
            {renderDone && showRender && (
              <div key={`${shown.id}-render`} data-testid="cutlist-render-player">
                {/* video.js runs in fill mode: the wrapper's size comes from the render's own aspect ratio */}
                <div className="ui-monitor__frame" style={{ width: renderRec.width && renderRec.height && renderRec.width > renderRec.height ? '100%' : 270, aspectRatio: renderRec.width && renderRec.height ? `${renderRec.width} / ${renderRec.height}` : '9 / 16' }}>
                  <VideoPlayer key={renderRec.jobId} src={renderRec.url} />
                </div>
              </div>
            )}
            {/* B-3: one line, the card's original formats, and its edit control */}
            {origLine && (
              <span className="ui-cluster" data-testid="cutlist-originals" data-braw={orig.braw} data-total={orig.total}>
                <span className="ui-dense ui-text-2">{origLine}</span>
                {orig.canEdit && !ro && <Button variant="quiet" data-testid="cutlist-originals-edit" data-open={origOpen ? 'true' : 'false'} aria-expanded={origOpen} onClick={() => setOrigOpen(o => !o)}>{origOpen ? 'Done' : 'Edit'}</Button>}
              </span>
            )}
            {origOpen && orig && orig.canEdit && !ro && (
              <div data-testid="cutlist-originals-checklist" className="ui-region ui-region--pad ui-stack ui-stack--tight">
                {/* file names exactly as uploaded: the basename's case is the point */}
                {orig.clips.map(c => (
                  <span key={c.id} className="ui-cluster">
                    <Checkbox label={<span className="ui-tc">{c.fileName}</span>} data-testid="cutlist-originals-clip" data-file={c.fileName} checked={c.originalExt === 'braw'} disabled={origBusy || !c.proxyOk}
                      onChange={e => setOriginals([c.id], e.target.checked ? 'braw' : null)} />
                    {!c.proxyOk && <span className="ui-meta">Not an .mp4</span>}
                  </span>
                ))}
                <span className="ui-cluster">
                  <Button variant="secondary" data-testid="cutlist-originals-all" disabled={origBusy || !orig.clips.some(c => c.proxyOk && c.originalExt !== 'braw')} onClick={() => setOriginals(orig.clips.filter(c => c.proxyOk && c.originalExt !== 'braw').map(c => c.id), 'braw')}>Mark all BRAW</Button>
                  <Button variant="quiet" data-testid="cutlist-originals-clear" disabled={origBusy || !hasBraw} onClick={() => setOriginals(orig.clips.filter(c => c.originalExt === 'braw').map(c => c.id), null)}>Clear</Button>
                </span>
                <span data-testid="cutlist-originals-help" className="ui-meta">A BRAW proxy must keep the original's exact name, frame rate and length. A renamed proxy cannot be re-pointed; a different frame rate misplaces every cut.</span>
                {origErr && <span data-testid="cutlist-originals-error" className="ui-field__error" role="alert">{clean(origErr)}</span>}
              </div>
            )}
            {/* R-1: a failed render is one plain line; nothing new was stored (a previous preview, if any, is still the one above) */}
            {!ro && (renderStartErr || (renderRec && (renderRec.status === 'failed' || renderRec.status === 'canceled' || renderRec.lastFailure))) && (
              <span data-testid="cutlist-render-error" className="ui-field__error" role="alert">
                Render failed: {clean(firstLine(renderStartErr || (renderRec.lastFailure && renderRec.lastFailure.error) || renderRec.error || renderRec.status).slice(0, 200))}
              </span>
            )}
          </section>
        )}
        {data && !shown && <p className="ui-p ui-dense ui-text-2">No scrub yet. Press Scrub to build the first cut list from the footage.</p>}

        {/* E-2: "Tell the editor what to fix": up to 500 characters, Scrub beside it; blank is a plain scrub */}
        {data && act && (
          <section data-testid="scrub-box" className="ui-stack ui-stack--tight" aria-label="Scrub">
            <label className="ui-field">
              <span className="ui-field__label">Tell the editor what to fix</span>
              <textarea className="ui-input ui-textarea" data-testid="scrub-correction" value={correction} maxLength={MAX_CORRECTION} rows={3} disabled={running || queued}
                placeholder="For example: row 12, use the take where she laughs; drop the B-roll on row 4" onChange={e => setCorrection(e.target.value.slice(0, MAX_CORRECTION))} />
            </label>
            <span className="ui-cluster">
              <Button variant="secondary" icon={RotateCcw} data-testid="scene-scrub-build" data-running={running ? 'true' : 'false'} data-queued={queued ? 'true' : 'false'} disabled={running || queued}
                onClick={() => scrub()} title={rangeNote ? `Scrub ${rangeNote}` : 'Build a new version from the footage, with your correction when the box has text'}>
                {running ? `Scrubbing ${fmtElapsed(elapsed)}` : queued ? `Queued, position ${slot.position || 1}` : 'Scrub'}
              </Button>
              {queued && slot.canCancel && <Button variant="quiet" data-testid="scrub-cancel" onClick={cancelQueued}>Cancel</Button>}
              {/* D-185: Producer and up get the menu for Assembly; the fresh presses stay Admin */}
              {(isAdmin || (ASSEMBLY && data?.canAssembly)) && !running && !queued && moreItems.length > 0 && (
                <Popover open={moreOpen} onOpenChange={setMoreOpen} align="end" label="More scrub options"
                  trigger={({ toggle, ref, props }) => (
                    <Button ref={ref} variant="quiet" icon={ChevronDown} data-testid="scrub-more" title="More scrub options" onClick={toggle} {...props}>More</Button>
                  )}>
                  <div data-testid="scrub-more-menu"><Menu label="More scrub options" items={moreItems} onClose={() => setMoreOpen(false)} /></div>
                </Popover>
              )}
            </span>
          </section>
        )}
        {scrubErr && <span data-testid="scrub-error" className="ui-field__error" role="alert">{clean(scrubErr)}</span>}
        {/* A refused press: one line and, when every open item can be acknowledged, Scrub anyway (same body as acknowledging each). */}
        {act && refusal && (
          <div data-testid="scrub-refused" className="ui-stack ui-stack--tight" role="alert">
            <p className="ui-p ui-dense">The scrub could not start. {clean(firstLine(refusal.body.error || ''))}</p>
            {ackable && <span><Button variant="secondary" data-testid="scrub-anyway" disabled={running || queued} onClick={() => setAnywayAsk(true)}>Scrub anyway</Button></span>}
          </div>
        )}

        {/* C-2: the older scrubs. A row: date, vN, the correction it was built from; View opens it read-only */}
        {(data?.cutLists?.length || 0) > 1 && (
          <section data-testid="cutlist-history" className="ui-stack ui-stack--tight" aria-label="Older scrubs">
            <span>
              <button type="button" className="ui-disclosure ui-dense ui-text-2" data-testid="cutlist-history-toggle" data-open={histOpen ? 'true' : 'false'} aria-expanded={histOpen} onClick={() => setHistOpen(o => !o)}>
                <Icon icon={ChevronDown} /><Icon icon={History} />Scrubs ({data.cutLists.length - 1} older)
              </button>
            </span>
            {histOpen && (
              <ul className="ui-plainlist">
                {data.cutLists.slice(1).map(v => (
                  <li key={v.id} data-testid="cutlist-history-row" data-version={v.version} className="ui-dockrow">
                    <span data-testid="cutlist-history-line" className="ui-dense">
                      <span className="ui-tc">v{v.version}</span> <span className="ui-meta">{fmtDT(v.builtAt)}</span>
                      {v.stats?.mode === 'assembly' && <span data-testid="cutlist-history-assembly" className="ui-text-2">{assemblyNote(v)}</span>}
                      {correctionOf(v) ? <span data-testid="cutlist-history-correction" className="ui-text-2">, correction: “{correctionOf(v)}”</span> : null}
                    </span>
                    <span className="ui-cluster">
                      {viewing === v.version
                        ? <span data-testid="cutlist-history-viewing"><Badge tone="warn">Viewing</Badge></span>
                        : <Button variant="quiet" icon={Eye} data-testid="cutlist-history-view" onClick={() => setViewing(v.version)}>View</Button>}
                      {xmlButtons(v, true)}
                      {v.stats?.render?.status === 'done' && v.stats.render.url && (
                        <a data-testid="cutlist-history-render-download" className="ui ui-btn ui-btn--quiet" href={v.stats.render.url} target="_blank" rel="noreferrer" download><Icon icon={Download} />MP4</a>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>

      {assemblyAsk && (
        <Modal title="Fresh assembly scrub" onClose={() => setAssemblyAsk(false)} testId="scrub-assembly-dialog"
          footer={<>
            <Button variant="quiet" data-testid="scrub-assembly-cancel" onClick={() => setAssemblyAsk(false)}>Cancel</Button>
            <Button variant="primary" data-testid="scrub-assembly-confirm" onClick={() => { setAssemblyAsk(false); scrub(null, true, true); }}>Start fresh assembly</Button>
          </>}>
          <p className="ui-p" data-testid="scrub-assembly-text">Assembles from scratch by the shoot rules: nothing reused from earlier assembly versions, editor picks not read. Only ambiguous spans and unfound lines reach a small model; usually well under $1.</p>
        </Modal>
      )}
      {anywayAsk && refusal && (
        <Modal title="Scrub anyway" onClose={() => setAnywayAsk(false)} testId="scrub-anyway-dialog"
          footer={<>
            <Button variant="quiet" data-testid="scrub-anyway-cancel" onClick={() => setAnywayAsk(false)}>Cancel</Button>
            <Button variant="primary" data-testid="scrub-anyway-confirm" disabled={running || queued}
              onClick={() => { setAnywayAsk(false); scrub(acknowledgeAll(refusal.body), refusal.fresh, refusal.assembly); }}>Scrub anyway</Button>
          </>}>
          <div className="ui-stack" data-testid="scrub-anyway-items">
            <p className="ui-p">The scrub starts with these acknowledged.</p>
            {acknowledgedNames(refusal.body).map(g => (
              <div key={g.label} className="ui-stack ui-stack--tight">
                <span className="ui-field__label">{g.label}</span>
                <p className="ui-p">{g.names.map(n => clean(n)).join(', ')}</p>
              </div>
            ))}
          </div>
        </Modal>
      )}
      {freshAsk && (
        <Modal title="Fresh scrub" onClose={() => setFreshAsk(false)} testId="scrub-fresh-dialog"
          footer={<>
            <Button variant="quiet" data-testid="scrub-fresh-cancel" onClick={() => setFreshAsk(false)}>Cancel</Button>
            <Button variant="primary" data-testid="scrub-fresh-confirm" onClick={() => { setFreshAsk(false); scrub(null, true); }}>Start fresh scrub</Button>
          </>}>
          <p className="ui-p" data-testid="scrub-fresh-text">Starts from scratch: nothing reused from earlier versions, editor picks not applied. Typically costs $1 to $4 in model usage; the hard safety limit is $20.</p>
        </Modal>
      )}
    </div>
  );
}
